import { faSplit, faTrash } from '@fortawesome/pro-light-svg-icons';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import IconButton from 'components/ui/IconButton';
import { styled } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ReturnItem, ReturnOrderLine } from 'returnCenter-api';
import { CellInputRefs } from 'hooks/useKeyboardNavigation';

type ShippedQtyCellProps = {
    item: ReturnItem;
    canSplit: boolean;
    handleAddSplitItem?: (
        orderNumber: string,
        item: ReturnItem,
        itemId: number | string,
        orderline: ReturnOrderLine,
    ) => void;
    itemId?: number | string;
    disabled?: boolean;
    preventRemove?: boolean;
    idx: number;
    handleRemoveSplit?: (
        sku: string,
        orderNumber: string,
        orderLineNumber: string,
        splitId: string | number,
        rowIndex?: number,
        colIndex?: number,
        cellRefPrefix?: string,
    ) => void;
    splitId?: string;
    orderline?: ReturnOrderLine;
    rowIndex?: string | number;
    colIndex?: string | number;
    cellInputRefs?: CellInputRefs;
    handleArrowKeyDown?: (e: any, orderId: any, rowIndex: any, colIndex: any) => void;
    cellRefPrefix?: string;
};

const ShippedQtyCell = ({
    item,
    canSplit,
    itemId,
    handleAddSplitItem,
    idx,
    handleRemoveSplit,
    splitId,
    orderline,
    rowIndex,
    colIndex,
    cellInputRefs,
    handleArrowKeyDown,
    cellRefPrefix = '',
    disabled = false,
    preventRemove = false,
}: ShippedQtyCellProps) => {
    const { t } = useTranslation();

    if (disabled) {
        return (
            <QuantityWrapper align="center" $grow={1} className="quantity-icon-wrapper" gap="4px" justify="start">
                <Text fontSize={12} bold as="span">
                    {`${orderline?.quantityShipped ?? 'N/A'}`}
                </Text>
            </QuantityWrapper>
        );
    }

    return (
        <QuantityWrapper
            key={itemId}
            align="center"
            $grow={1}
            className="quantity-icon-wrapper"
            disable={disabled}
            gap="4px"
            data-row={rowIndex}
            data-row-cell={colIndex}
            data-item-id={itemId}
            justify="start"
        >
            {idx === 0 && (
                <Text fontSize={12} bold className={!canSplit ? 'ml-1' : ''} as="span">
                    {`${orderline?.quantityShipped ?? 'N/A'}`}
                </Text>
            )}

            {canSplit && idx === 0 && (
                <IconContainer className="ml-1" align="center" justify="end" $grow={1}>
                    <StyledIconButon
                        className={canSplit ? 'split-button cursor-pointer ml-2' : ''}
                        disabled={!canSplit}
                        icon={faSplit}
                        title={t('splitItem', 'Split item')}
                        onClick={() =>
                            handleAddSplitItem?.(orderline?.orderNumber ?? '', item, itemId ?? '', orderline ?? {})
                        }
                        tabIndex={0}
                        ref={(el) => {
                            const key = `${cellRefPrefix}${rowIndex}-${colIndex}`;
                            if (cellInputRefs) cellInputRefs.current[key] = el;
                        }}
                        onKeyDown={handleArrowKeyDown ? (e) => handleArrowKeyDown(e, itemId, rowIndex, colIndex) : undefined}
                    ></StyledIconButon>
                </IconContainer>
            )}

            {idx > 0 && !preventRemove && (
                <IconContainer $grow={1} justify="end">
                    <StyledIconButon
                        margin="4px"
                        className="split cursor-pointer ml-2"
                        iconSize="sm"
                        type="button"
                        icon={faTrash}
                        title={t('remove')}
                        onClick={() => {
                            handleRemoveSplit?.(
                                orderline?.sku ?? '',
                                orderline?.orderNumber ?? '',
                                orderline?.orderLineNumber ?? '',
                                splitId ?? itemId ?? item.id ?? '',
                                rowIndex as number,
                                colIndex as number,
                                cellRefPrefix,
                            );
                        }}
                        tabIndex={0}
                        ref={(el) => {
                            const key = `${cellRefPrefix}${rowIndex}-${colIndex}`;
                            if (cellInputRefs) cellInputRefs.current[key] = el;
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleRemoveSplit?.(
                                    orderline?.sku ?? '',
                                    orderline?.orderNumber ?? '',
                                    orderline?.orderLineNumber ?? '',
                                    splitId ?? itemId ?? item.id ?? '',
                                    rowIndex as number,
                                    colIndex as number,
                                    cellRefPrefix,
                                );
                                return;
                            }
                            handleArrowKeyDown?.(e, itemId, rowIndex, colIndex);
                        }}
                    />
                </IconContainer>
            )}
        </QuantityWrapper>
    );
};

export default ShippedQtyCell;

const QuantityWrapper = styled(Flex)<{ margin?: string; position?: string; disable?: boolean }>`
    ${({ margin, position }) => margin && `margin-${position}: ${margin};`}
    ${({ disable }) => disable && 'opacity: 0.5; pointer-events: none;'}
    max-width: 120px;
`;

const StyledIconButon = styled(IconButton)<{ margin?: string }>`
    ${({ margin }) => margin && `margin-left: ${margin};`}
`;

const IconContainer = styled(Flex)``;
